import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, CreditCard, Loader2, AlertCircle, TrendingUp, UserPlus, Smartphone, Filter, ChevronDown, ChevronRight, Eye, EyeOff, Save, CheckCircle, Settings, Zap, ZapOff, Wifi, WifiOff, Car, Crown, ToggleLeft, ToggleRight, Download, RefreshCw, Database, Receipt, Activity, Search, Users2, Repeat, CalendarDays, Clock } from 'lucide-react';
import { fetchSystemConfig, saveSystemConfig, testVippsConnection, getRevenueReport, runRevenueBackfill, runRevenueReconciliation, getSubscriberPayments } from '../../../services/firebaseService';
import type { RevenueReport, SubscriberPayment } from '../../../services/firebaseService';

type ProviderFilter = 'all' | 'stripe' | 'vipps' | 'apple';

const AdminRevenueDashboard: React.FC = () => {
    const { t } = useTranslation();
    const [providerFilter, setProviderFilter] = useState<ProviderFilter>('all');

    // Vipps config state
    const [vippsConfigOpen, setVippsConfigOpen] = useState(false);
    const [vippsConfig, setVippsConfig] = useState({ clientId: '', clientSecret: '', subscriptionKey: '', msn: '' });
    const [vippsEnabled, setVippsEnabled] = useState(false);
    const [vippsConfigLoaded, setVippsConfigLoaded] = useState(false);
    const [showSecrets, setShowSecrets] = useState(false);
    const [savingVipps, setSavingVipps] = useState(false);
    const [vippsSaved, setVippsSaved] = useState(false);
    const [testingVipps, setTestingVipps] = useState(false);
    const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

    // Subscription price state
    const [monthlyPrice, setMonthlyPrice] = useState(49);
    const [priceInput, setPriceInput] = useState('49');
    const [savingPrice, setSavingPrice] = useState(false);
    const [priceSaved, setPriceSaved] = useState(false);

    // Premium tier state
    const [premiumEnabled, setPremiumEnabled] = useState(false);
    const [premiumPriceInput, setPremiumPriceInput] = useState('99');
    const [premiumStripePriceId, setPremiumStripePriceId] = useState('');
    const [premiumFeatures, setPremiumFeatures] = useState<string[]>(['android_auto', 'carplay']);
    const [savingPremium, setSavingPremium] = useState(false);
    const [premiumSaved, setPremiumSaved] = useState(false);
    const [premiumLoaded, setPremiumLoaded] = useState(false);

    // Valutakurser (system/fx_rates) — NOK per 1 enhet
    const [fxRates, setFxRates] = useState<Record<string, string>>({ USD: '', EUR: '' });
    const [fxLoaded, setFxLoaded] = useState(false);
    const [savingFx, setSavingFx] = useState(false);
    const [fxSaved, setFxSaved] = useState(false);
    const [newFxCode, setNewFxCode] = useState('');

    // Reell inntektsrapport (server-aggregert)
    const [report, setReport] = useState<RevenueReport | null>(null);
    const [reportLoading, setReportLoading] = useState(true);
    const [reportError, setReportError] = useState<string | null>(null);
    const [backfilling, setBackfilling] = useState(false);
    const [backfillResult, setBackfillResult] = useState<string | null>(null);
    const [reconciling, setReconciling] = useState(false);
    const [expandedUid, setExpandedUid] = useState<string | null>(null);
    const [userPayments, setUserPayments] = useState<Record<string, SubscriberPayment[]>>({});
    const [loadingPayments, setLoadingPayments] = useState<string | null>(null);
    const [subSearch, setSubSearch] = useState('');
    const [activeOnly, setActiveOnly] = useState(false);

    // ── Medlemsinnsikt: levetid før churn, gjentagende kunder, sesongmønster ──
    // Alt avledet klientside fra report.subscribers (samme sannhetskilde som resten).
    const memberInsights = useMemo(() => {
        if (!report) return null;
        const now = report.generatedAt || Date.now();
        const MS_MONTH = 30 * 24 * 60 * 60 * 1000;

        // 1) Betalende levetid før churn (kun avsluttede med reell betalingshistorikk)
        const lifespans: number[] = [];
        report.subscribers.forEach(s => {
            if (s.active) return;                                  // fortsatt aktiv → ikke churnet
            if ((s.txCount || 0) < 1 || !s.startDate) return;      // aldri reell betalende
            const end = s.currentPeriodEnd || s.lastPaymentDate || s.startDate;
            lifespans.push(Math.max(1, Math.round((end - s.startDate) / MS_MONTH)));
        });
        lifespans.sort((a, b) => a - b);
        const churnCount = lifespans.length;
        const avgLifespan = churnCount ? lifespans.reduce((a, b) => a + b, 0) / churnCount : 0;
        const medianLifespan = churnCount
            ? (churnCount % 2 ? lifespans[(churnCount - 1) / 2] : (lifespans[churnCount / 2 - 1] + lifespans[churnCount / 2]) / 2)
            : 0;
        const lifespanBuckets = [
            { label: '0–1 mnd', min: 0, max: 1, count: 0 },
            { label: '2–3 mnd', min: 2, max: 3, count: 0 },
            { label: '4–6 mnd', min: 4, max: 6, count: 0 },
            { label: '7–12 mnd', min: 7, max: 12, count: 0 },
            { label: '12+ mnd', min: 13, max: Infinity, count: 0 },
        ];
        lifespans.forEach(m => { const b = lifespanBuckets.find(b => m >= b.min && m <= b.max); if (b) b.count++; });
        const maxBucket = Math.max(...lifespanBuckets.map(b => b.count), 1);

        // 2) Gjentagende kunder (blant abonnenter med betalingshistorikk)
        const withHistory = report.subscribers.filter(s => (s.txCount || 0) >= 1);
        const recurringCount = withHistory.filter(s => (s.txCount || 0) > 1).length;
        const oneTimeCount = withHistory.length - recurringCount;
        const recurringPct = withHistory.length ? (recurringCount / withHistory.length) * 100 : 0;
        const avgRenewals = withHistory.length
            ? withHistory.reduce((a, s) => a + (s.txCount || 0), 0) / withHistory.length
            : 0;

        // 3) Sesongmønster: snitt aktive medlemmer per kalendermåned (jan–des), snittet over år
        const activeByYm: Record<string, number> = {};
        report.subscribers.forEach(s => {
            if (!s.startDate) return;
            if ((s.txCount || 0) < 1 && !s.active) return;         // aldri reelt medlem
            const start = s.startDate;
            const end = Math.min(s.active ? now : (s.currentPeriodEnd || s.lastPaymentDate || s.startDate), now);
            if (end < start) return;
            const d = new Date(start); d.setDate(1);
            const endD = new Date(end);
            let guard = 0;
            while (d <= endD && guard++ < 600) {
                const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                activeByYm[ym] = (activeByYm[ym] || 0) + 1;
                d.setMonth(d.getMonth() + 1);
            }
        });
        const monthNames = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
        const acc = monthNames.map(name => ({ name, sum: 0, years: 0 }));
        Object.entries(activeByYm).forEach(([ym, count]) => {
            const mi = parseInt(ym.slice(5, 7), 10) - 1;
            acc[mi].sum += count;
            acc[mi].years += 1;
        });
        const seasonality = acc.map(m => ({ name: m.name, avg: m.years ? m.sum / m.years : 0, hasData: m.years > 0 }));
        const maxSeason = Math.max(...seasonality.map(m => m.avg), 1);
        const seasonWithData = seasonality.filter(m => m.hasData);
        const peakSeason = seasonWithData.length
            ? seasonWithData.reduce((best, m) => (m.avg > best.avg ? m : best))
            : null;

        return {
            churnCount, avgLifespan, medianLifespan, lifespanBuckets, maxBucket,
            recurringCount, oneTimeCount, withHistoryCount: withHistory.length, recurringPct, avgRenewals,
            seasonality, maxSeason, peakSeason,
        };
    }, [report]);

    useEffect(() => {
        fetchSystemConfig('subscription_config').then(data => {
            if (data?.priceNok && Number.isFinite(data.priceNok)) {
                setMonthlyPrice(data.priceNok);
                setPriceInput(String(data.priceNok));
            }
        }).catch(() => { });
        fetchSystemConfig('premium_config').then(data => {
            if (data) {
                setPremiumEnabled(data.enabled === true);
                if (data.priceNok) setPremiumPriceInput(String(data.priceNok));
                if (data.stripePriceId) setPremiumStripePriceId(data.stripePriceId);
                if (data.features) setPremiumFeatures(data.features);
            }
            setPremiumLoaded(true);
        }).catch(() => setPremiumLoaded(true));
        fetchSystemConfig('fx_rates').then(data => {
            const rates = data?.rates && typeof data.rates === 'object' ? data.rates : {};
            const asStr: Record<string, string> = {};
            Object.entries(rates).forEach(([k, v]) => { asStr[k] = String(v); });
            if (!asStr.USD) asStr.USD = '';
            if (!asStr.EUR) asStr.EUR = '';
            setFxRates(asStr);
            setFxLoaded(true);
        }).catch(() => setFxLoaded(true));
        loadReport();
    }, []);

    // Lazy-load vipps config when section is opened
    useEffect(() => {
        if (vippsConfigOpen && !vippsConfigLoaded) {
            fetchSystemConfig('vipps_config').then(data => {
                if (data) {
                    setVippsConfig({
                        clientId: data.clientId || '',
                        clientSecret: data.clientSecret || '',
                        subscriptionKey: data.subscriptionKey || '',
                        msn: data.msn || '',
                    });
                    setVippsEnabled(data.enabled === true);
                }
                setVippsConfigLoaded(true);
            }).catch(() => setVippsConfigLoaded(true));
        }
    }, [vippsConfigOpen, vippsConfigLoaded]);

    const handleSaveVippsConfig = async () => {
        setSavingVipps(true);
        setTestResult(null);
        try {
            await saveSystemConfig('vipps_config', { ...vippsConfig, enabled: vippsEnabled });
            setVippsSaved(true);
            setTimeout(() => setVippsSaved(false), 3000);
        } catch (e) {
            console.error('Failed to save Vipps config:', e);
        } finally {
            setSavingVipps(false);
        }
    };

    const handleToggleVipps = async (enabled: boolean) => {
        setVippsEnabled(enabled);
        try {
            await saveSystemConfig('vipps_config', { ...vippsConfig, enabled });
            setVippsSaved(true);
            setTimeout(() => setVippsSaved(false), 3000);
        } catch (e) {
            setVippsEnabled(!enabled); // rollback
            console.error('Failed to toggle Vipps:', e);
        }
    };

    const handleTestVipps = async () => {
        setTestingVipps(true);
        setTestResult(null);
        try {
            const result = await testVippsConnection();
            setTestResult(result);
        } catch (e: any) {
            setTestResult({ ok: false, error: e.message || 'Kunne ikke kjøre test.' });
        } finally {
            setTestingVipps(false);
        }
    };

    const handleSavePrice = async () => {
        const newPrice = parseFloat(priceInput);
        if (!Number.isFinite(newPrice) || newPrice <= 0) return;
        setSavingPrice(true);
        try {
            await saveSystemConfig('subscription_config', { priceNok: newPrice, currency: 'NOK', interval: 'month', productName: 'Fricamp Supporter', updatedAt: new Date().toISOString() });
            setMonthlyPrice(newPrice);
            setPriceSaved(true);
            setTimeout(() => setPriceSaved(false), 2000);
            loadReport(); // Oppdater rapport (ny pris → ny est. MRR)
        } catch (e) {
            console.error('Failed to save price:', e);
        } finally {
            setSavingPrice(false);
        }
    };

    const handleAddFxCurrency = () => {
        const code = newFxCode.trim().toUpperCase();
        if (!code || !/^[A-Z]{3}$/.test(code) || code === 'NOK' || fxRates[code] !== undefined) return;
        setFxRates(prev => ({ ...prev, [code]: '' }));
        setNewFxCode('');
    };

    const handleRemoveFxCurrency = (code: string) => {
        setFxRates(prev => {
            const next = { ...prev };
            delete next[code];
            return next;
        });
    };

    const handleSaveFxRates = async () => {
        setSavingFx(true);
        try {
            const rates: Record<string, number> = {};
            Object.entries(fxRates).forEach(([code, val]) => {
                const n = parseFloat(val);
                if (Number.isFinite(n) && n > 0) rates[code] = n;
            });
            await saveSystemConfig('fx_rates', { rates, updatedAt: new Date().toISOString() });
            setFxSaved(true);
            setTimeout(() => setFxSaved(false), 2000);
            loadReport(); // Reberegn amountNok i rapporten (cache i backend er 5 min)
        } catch (e) {
            console.error('Failed to save fx rates:', e);
        } finally {
            setSavingFx(false);
        }
    };

    const handleSavePremiumConfig = async () => {
        const price = parseFloat(premiumPriceInput);
        if (!Number.isFinite(price) || price <= 0) return;
        setSavingPremium(true);
        try {
            await saveSystemConfig('premium_config', {
                enabled: premiumEnabled,
                priceNok: price,
                stripePriceId: premiumStripePriceId,
                revenuecatEntitlementId: 'Fricamp Premium',
                features: premiumFeatures,
                description: {
                    no: 'Vis Fricamps kart på bilskjermen via Android Auto og CarPlay',
                    en: 'Show Fricamp maps on your car screen via Android Auto and CarPlay',
                },
                updatedAt: new Date().toISOString(),
            });
            setPremiumSaved(true);
            setTimeout(() => setPremiumSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save premium config:', e);
        } finally {
            setSavingPremium(false);
        }
    };

    const togglePremiumFeature = (f: string) => {
        setPremiumFeatures(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
    };

    const vippsConfigComplete = !!(vippsConfig.clientId && vippsConfig.clientSecret && vippsConfig.subscriptionKey && vippsConfig.msn);

    // ── Alt under er nå avledet fra ÉN kilde: getRevenueReport (report) ──
    const totalRevenue = () => report ? report.revenue.yearly.reduce((s, y) => s + y.total, 0) : 0;

    const getFilteredTotal = () => {
        if (!report) return 0;
        if (providerFilter === 'stripe') return report.counts.stripePaid;
        if (providerFilter === 'vipps') return report.counts.vippsPaid;
        if (providerFilter === 'apple') return report.counts.applePaid;
        return report.counts.activePaying;
    };

    const getFilteredMrr = () => {
        if (!report) return 0;
        if (providerFilter === 'all') return report.estimatedMrr;
        return getFilteredTotal() * monthlyPrice;
    };

    const conversionRate = () => {
        if (!report) return 0;
        const denom = report.counts.activePaying + report.counts.free;
        return denom > 0 ? (report.counts.activePaying / denom) * 100 : 0;
    };

    const loadReport = async () => {
        setReportLoading(true);
        setReportError(null);
        try {
            const data = await getRevenueReport();
            setReport(data);
        } catch (e: any) {
            console.error('getRevenueReport error:', e);
            setReportError(e?.message || 'Kunne ikke hente inntektsrapport.');
        } finally {
            setReportLoading(false);
        }
    };

    const handleBackfill = async () => {
        if (!window.confirm('Tilbakefyll transaksjoner og hendelser fra Stripe, Vipps og Apple?\n\nDette henter faktisk betalingshistorikk fra API-ene. Trygt å kjøre flere ganger (idempotent).')) return;
        setBackfilling(true);
        setBackfillResult(null);
        try {
            const r = await runRevenueBackfill();
            setBackfillResult(`Ferdig: ${r.txCount} transaksjoner, ${r.eventCount} hendelser fra ${r.scannedUsers} brukere (Stripe ${r.stripeUsers}, Vipps ${r.vippsUsers}, Apple ${r.appleUsers}). ${r.errorCount > 0 ? `${r.errorCount} feil.` : ''}`);
            await loadReport();
        } catch (e: any) {
            console.error('backfill error:', e);
            setBackfillResult(`Feil: ${e?.message || 'Backfill feilet.'}`);
        } finally {
            setBackfilling(false);
        }
    };

    const handleReconcile = async () => {
        setReconciling(true);
        setBackfillResult(null);
        try {
            const r = await runRevenueReconciliation();
            setBackfillResult(`Avstemt: ${r.appleChecked} Apple sjekket — ${r.applePurchased} betaler, ${r.appleFamilyShared} familiedeling (gratis). Apple proceeds: $${r.appleProceedsUsd?.toFixed(2)} (etter Apple-kutt). ${r.txRecomputed} transaksjoner rekalkulert. ${r.appleErrors > 0 ? `${r.appleErrors} feil.` : ''}`);
            await loadReport();
        } catch (e: any) {
            console.error('reconcile error:', e);
            setBackfillResult(`Feil: ${e?.message || 'Avstemming feilet.'}`);
        } finally {
            setReconciling(false);
        }
    };

    const toggleSubscriber = async (uid: string) => {
        if (expandedUid === uid) { setExpandedUid(null); return; }
        setExpandedUid(uid);
        if (!userPayments[uid]) {
            setLoadingPayments(uid);
            try {
                const { transactions } = await getSubscriberPayments(uid);
                setUserPayments(prev => ({ ...prev, [uid]: transactions }));
            } catch (e) {
                console.error('getSubscriberPayments error:', e);
                setUserPayments(prev => ({ ...prev, [uid]: [] }));
            } finally {
                setLoadingPayments(null);
            }
        }
    };

    const fmtNok = (n: number | null | undefined) => n == null ? '–' : `kr ${Math.round(n).toLocaleString('nb-NO')}`;
    const fmtDate = (ms: number | null | undefined) => ms ? new Date(ms).toLocaleDateString('nb-NO', { year: 'numeric', month: 'short', day: 'numeric' }) : '–';
    const monthsActive = (start: number | null, end: number | null) => {
        if (!start) return 0;
        const to = end && end < Date.now() ? end : Date.now();
        return Math.max(1, Math.round((to - start) / (30 * 24 * 60 * 60 * 1000)));
    };
    const providerLabel = (p: string | null) => p === 'vipps' ? 'Vipps' : p === 'apple' ? 'Apple' : p === 'stripe' ? 'Stripe' : '–';

    // CSV-eksport: alle transaksjoner per abonnent + månedlig oppsummering
    const downloadCsv = (filename: string, rows: (string | number)[][]) => {
        const escape = (v: string | number) => {
            const s = String(v ?? '');
            return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        // Semikolon-separator + BOM for norsk Excel
        const csv = '﻿' + rows.map(r => r.map(escape).join(';')).join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportMonthlyCsv = () => {
        if (!report) return;
        const rows: (string | number)[][] = [['Måned', 'Stripe (kr)', 'Vipps (kr)', 'Apple (kr)', 'Total (kr)', 'Antall betalinger']];
        report.revenue.monthly.forEach(m => rows.push([m.month, Math.round(m.stripe), Math.round(m.vipps), Math.round(m.apple), Math.round(m.total), m.count]));
        const tot = report.revenue.monthly.reduce((a, m) => ({ s: a.s + m.stripe, v: a.v + m.vipps, ap: a.ap + m.apple, t: a.t + m.total, c: a.c + m.count }), { s: 0, v: 0, ap: 0, t: 0, c: 0 });
        rows.push(['SUM', Math.round(tot.s), Math.round(tot.v), Math.round(tot.ap), Math.round(tot.t), tot.c]);
        downloadCsv(`fricamp-inntekt-maaned-${new Date().toISOString().slice(0, 10)}.csv`, rows);
    };

    const exportTransactionsCsv = async () => {
        if (!report) return;
        // Hent transaksjoner for alle abonnenter med betalingshistorikk
        const subs = report.subscribers.filter(s => s.txCount > 0);
        const all: (string | number)[][] = [['Dato', 'Bruker', 'E-post', 'Tjeneste', 'Type', 'Beløp', 'Valuta', 'Beløp (NOK)', 'Status', 'Beskrivelse']];
        for (const s of subs) {
            let txs = userPayments[s.uid];
            if (!txs) {
                try { txs = (await getSubscriberPayments(s.uid)).transactions; setUserPayments(prev => ({ ...prev, [s.uid]: txs! })); }
                catch { txs = []; }
            }
            txs.forEach(t => all.push([
                new Date(t.date).toISOString().slice(0, 10),
                s.name || s.uid, s.email || '',
                providerLabel(t.provider), t.type,
                t.amount, t.currency,
                t.amountNok != null ? Math.round(t.amountNok) : '',
                t.status, t.description,
            ]));
        }
        downloadCsv(`fricamp-transaksjoner-${new Date().toISOString().slice(0, 10)}.csv`, all);
    };

    if (reportLoading && !report) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Loader2 size={24} className="animate-spin text-[var(--text-muted)]" />
                <p className="text-xs text-[var(--text-muted)] font-medium tracking-wide uppercase">Laster omsetningsdata...</p>
            </div>
        );
    }

    if (reportError || !report) {
        return (
            <div className="p-6 bg-red-500/5 border border-red-500/10 rounded-2xl flex items-start gap-4">
                <AlertCircle className="text-red-400 shrink-0" size={18} />
                <div>
                    <p className="text-sm font-bold text-red-400">Data utilgjengelig</p>
                    <p className="text-xs text-red-400/70 mt-1">{reportError}</p>
                </div>
            </div>
        );
    }

    const newUsers = report.newUsersByMonth || [];
    const maxNewUsers = Math.max(...newUsers.map(m => m.count), 1);
    const monthLabel = (ym: string) => {
        const names = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
        const [y, m] = ym.split('-');
        return `${names[parseInt(m) - 1]} ${y.slice(2)}`;
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
            {/* Vipps API Config (collapsible) */}
            <div className={`border rounded-panel overflow-hidden transition-colors ${vippsEnabled ? 'bg-vipps/[0.03] border-vipps/20' : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)]'}`}>
                <button
                    onClick={() => setVippsConfigOpen(v => !v)}
                    className="w-full flex items-center justify-between p-5 hover:bg-[var(--bg-surface-alt)] transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${vippsEnabled ? 'bg-vipps/20' : 'bg-vipps/10'}`}>
                            <Settings size={16} className="text-vipps" />
                        </div>
                        <span className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Vipps-betaling</span>
                        {vippsConfigLoaded && (
                            <span className={`text-4xs font-black uppercase tracking-widest px-2 py-0.5 rounded-md border ${vippsEnabled ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border-[var(--border-secondary)]'}`}>
                                {vippsEnabled ? 'Aktiv' : 'Av'}
                            </span>
                        )}
                        {vippsSaved && (
                            <span className="flex items-center gap-1 text-3xs font-bold text-emerald-400 animate-in fade-in duration-300">
                                <CheckCircle size={12} /> Lagret
                            </span>
                        )}
                    </div>
                    <ChevronDown size={16} className={`text-[var(--text-muted)] transition-transform duration-200 ${vippsConfigOpen ? 'rotate-180' : ''}`} />
                </button>

                {vippsConfigOpen && (
                    <div className="px-5 pb-5 space-y-5 animate-in slide-in-from-top-2 duration-200">
                        {!vippsConfigLoaded ? (
                            <div className="flex items-center justify-center py-6">
                                <Loader2 size={16} className="animate-spin text-[var(--text-muted)]" />
                            </div>
                        ) : (
                            <>
                                {/* Enable/Disable Toggle */}
                                <div className="flex items-center justify-between bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-2xl p-4">
                                    <div className="flex items-center gap-3">
                                        {vippsEnabled ? <Zap size={16} className="text-vipps" /> : <ZapOff size={16} className="text-[var(--text-muted)]" />}
                                        <div>
                                            <p className="text-xs font-black text-[var(--text-primary)]">Vipps som betalingsmetode</p>
                                            <p className="text-2xs text-[var(--text-muted)]">
                                                {vippsEnabled ? 'Brukere kan velge Vipps ved oppgradering.' : 'Kun kortbetaling via Stripe er tilgjengelig.'}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleToggleVipps(!vippsEnabled)}
                                        className={`relative w-12 h-7 rounded-full transition-colors duration-200 ${vippsEnabled ? 'bg-vipps' : 'bg-[var(--bg-input)]'}`}
                                    >
                                        <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-200 ${vippsEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                    </button>
                                </div>

                                {/* API Keys */}
                                <div>
                                    <p className="text-2xs text-[var(--text-muted)] leading-relaxed mb-3">
                                        API-nøkler fra Vipps-portalen. Lagres i Firestore, leses av Cloud Functions med 5 min cache.
                                    </p>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {([
                                            { key: 'clientId', label: 'Client ID', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
                                            { key: 'clientSecret', label: 'Client Secret', placeholder: 'Din client secret' },
                                            { key: 'subscriptionKey', label: 'Ocp-Apim-Subscription-Key', placeholder: 'Din subscription key' },
                                            { key: 'msn', label: 'Merchant Serial Number (MSN)', placeholder: 'F.eks. 123456' },
                                        ] as const).map(field => (
                                            <div key={field.key}>
                                                <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest block mb-1.5">{field.label}</label>
                                                <input
                                                    type={showSecrets ? 'text' : 'password'}
                                                    value={vippsConfig[field.key]}
                                                    onChange={e => setVippsConfig(prev => ({ ...prev, [field.key]: e.target.value }))}
                                                    placeholder={field.placeholder}
                                                    className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2.5 text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] font-mono focus:outline-none focus:border-vipps/30 transition-colors"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Test result */}
                                {testResult && (
                                    <div className={`flex items-start gap-3 p-3 rounded-xl border ${testResult.ok ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
                                        {testResult.ok ? <Wifi size={14} className="text-emerald-400 mt-0.5 shrink-0" /> : <WifiOff size={14} className="text-red-400 mt-0.5 shrink-0" />}
                                        <div>
                                            <p className={`text-2xs font-bold ${testResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {testResult.ok ? 'Tilkobling OK — access token mottatt fra Vipps.' : 'Tilkobling feilet'}
                                            </p>
                                            {testResult.error && <p className="text-2xs text-red-400/70 mt-0.5">{testResult.error}</p>}
                                        </div>
                                    </div>
                                )}

                                {/* Action buttons */}
                                <div className="flex items-center gap-3 flex-wrap">
                                    <button
                                        onClick={() => setShowSecrets(v => !v)}
                                        className="flex items-center gap-1.5 text-3xs font-bold text-[var(--text-muted)] hover:text-[var(--text-secondary)] uppercase tracking-widest transition-colors"
                                    >
                                        {showSecrets ? <EyeOff size={12} /> : <Eye size={12} />}
                                        {showSecrets ? 'Skjul' : 'Vis'}
                                    </button>
                                    <div className="flex-1" />
                                    <button
                                        onClick={handleTestVipps}
                                        disabled={testingVipps || !vippsConfigComplete}
                                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] border border-[var(--border-secondary)] text-[var(--text-secondary)] text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50"
                                    >
                                        {testingVipps ? <Loader2 size={12} className="animate-spin" /> : <Wifi size={12} />}
                                        Test tilkobling
                                    </button>
                                    <button
                                        onClick={handleSaveVippsConfig}
                                        disabled={savingVipps}
                                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-vipps/10 hover:bg-vipps/20 border border-vipps/20 text-vipps text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-70"
                                    >
                                        {savingVipps ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                                        Lagre nøkler
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Subscription Price */}
            <div className="bg-blue-500/5 border border-blue-500/20 p-5 rounded-panel">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-xl bg-blue-500/20 flex items-center justify-center">
                            <CreditCard size={16} className="text-blue-400" />
                        </div>
                        <div>
                            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Abonnementspris</p>
                            <p className="text-4xs text-[var(--text-muted)]">Gjelder Vipps og visning i appen. Stripe-pris styres i Stripe Dashboard.</p>
                        </div>
                    </div>
                    {priceSaved && (
                        <span className="flex items-center gap-1 text-3xs font-bold text-emerald-400 animate-in fade-in duration-300">
                            <CheckCircle size={12} /> Lagret
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3 mt-4">
                    <div className="flex items-center gap-2 flex-1">
                        <span className="text-tiny font-bold text-[var(--text-muted)]">kr</span>
                        <input
                            type="number"
                            value={priceInput}
                            onChange={(e) => setPriceInput(e.target.value)}
                            min="1"
                            step="1"
                            className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2 text-[12px] font-bold text-[var(--text-primary)] focus:outline-none focus:border-blue-500 w-24"
                        />
                        <span className="text-tiny font-bold text-[var(--text-muted)]">/ mnd</span>
                    </div>
                    <button
                        onClick={handleSavePrice}
                        disabled={savingPrice || priceInput === String(monthlyPrice)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {savingPrice ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                        Lagre pris
                    </button>
                </div>
            </div>

            {/* Valutakurser (fx_rates) — for omregning av Apple-beløp til NOK */}
            {fxLoaded && (
                <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-panel">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center">
                                <TrendingUp size={16} className="text-amber-400" />
                            </div>
                            <div>
                                <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Valutakurser</p>
                                <p className="text-4xs text-[var(--text-muted)]">NOK per 1 enhet. Brukes til å regne Apple-beløp (USD/EUR osv.) om til NOK i inntektsrapporten.</p>
                            </div>
                        </div>
                        {fxSaved && (
                            <span className="flex items-center gap-1 text-3xs font-bold text-emerald-400 animate-in fade-in duration-300">
                                <CheckCircle size={12} /> Lagret
                            </span>
                        )}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4">
                        {Object.keys(fxRates).sort().map(code => (
                            <div key={code} className="flex items-center gap-2 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2">
                                <span className="text-tiny font-black text-[var(--text-secondary)] w-9">{code}</span>
                                <input
                                    type="number"
                                    value={fxRates[code]}
                                    onChange={e => setFxRates(prev => ({ ...prev, [code]: e.target.value }))}
                                    placeholder="kurs"
                                    min="0"
                                    step="0.01"
                                    className="flex-1 min-w-0 bg-transparent text-[12px] font-bold text-[var(--text-primary)] focus:outline-none"
                                />
                                <button onClick={() => handleRemoveFxCurrency(code)} className="text-sm leading-none text-[var(--text-muted)] hover:text-red-400 transition-colors" title="Fjern">
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center gap-3 mt-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <input
                                value={newFxCode}
                                onChange={e => setNewFxCode(e.target.value.toUpperCase().slice(0, 3))}
                                onKeyDown={e => { if (e.key === 'Enter') handleAddFxCurrency(); }}
                                placeholder="F.eks. GBP"
                                maxLength={3}
                                className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2 text-tiny font-mono uppercase text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-amber-500/30 w-28"
                            />
                            <button
                                onClick={handleAddFxCurrency}
                                disabled={!/^[A-Z]{3}$/.test(newFxCode.trim()) || newFxCode.trim().toUpperCase() === 'NOK'}
                                className="px-3 py-2 rounded-xl bg-[var(--bg-surface)] hover:bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-[var(--text-secondary)] text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-40"
                            >
                                + Valuta
                            </button>
                        </div>
                        <div className="flex-1" />
                        <button
                            onClick={handleSaveFxRates}
                            disabled={savingFx}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-amber-600/20 border border-amber-500/30 text-amber-400 hover:bg-amber-600/30 transition-all active:scale-95 disabled:opacity-40"
                        >
                            {savingFx ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                            Lagre kurser
                        </button>
                    </div>
                </div>
            )}

            {/* Premium Tier Config */}
            {premiumLoaded && (
                <div className={`border p-5 rounded-panel ${premiumEnabled ? 'bg-indigo-500/5 border-indigo-500/20' : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)]'}`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${premiumEnabled ? 'bg-indigo-500/20' : 'bg-[var(--bg-surface)]'}`}>
                                <Crown size={16} className={premiumEnabled ? 'text-indigo-400' : 'text-[var(--text-muted)]'} />
                            </div>
                            <div>
                                <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Premium-nivå</p>
                                <p className="text-4xs text-[var(--text-muted)]">Android Auto + CarPlay. Aktivér når funksjonen er klar.</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {premiumSaved && (
                                <span className="flex items-center gap-1 text-3xs font-bold text-emerald-400 animate-in fade-in duration-300">
                                    <CheckCircle size={12} /> Lagret
                                </span>
                            )}
                            <button
                                onClick={() => { setPremiumEnabled(e => !e); }}
                                className="flex items-center gap-1.5 text-3xs font-black uppercase tracking-widest transition-all"
                            >
                                {premiumEnabled ? (
                                    <><ToggleRight size={20} className="text-indigo-400" /> <span className="text-indigo-400">Aktiv</span></>
                                ) : (
                                    <><ToggleLeft size={20} className="text-[var(--text-muted)]" /> <span className="text-[var(--text-muted)]">Av</span></>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 space-y-3">
                        {/* Premium price */}
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 flex-1">
                                <span className="text-tiny font-bold text-[var(--text-muted)]">kr</span>
                                <input
                                    type="number"
                                    value={premiumPriceInput}
                                    onChange={(e) => setPremiumPriceInput(e.target.value)}
                                    min="1"
                                    step="1"
                                    className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2 text-[12px] font-bold text-[var(--text-primary)] focus:outline-none focus:border-indigo-500 w-24"
                                />
                                <span className="text-tiny font-bold text-[var(--text-muted)]">/ mnd</span>
                            </div>
                        </div>

                        {/* Stripe Price ID */}
                        <div className="flex items-center gap-2">
                            <span className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest whitespace-nowrap">Stripe Price ID:</span>
                            <input
                                type="text"
                                value={premiumStripePriceId}
                                onChange={(e) => setPremiumStripePriceId(e.target.value)}
                                placeholder="price_..."
                                className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2 text-tiny font-mono text-[var(--text-primary)] focus:outline-none focus:border-indigo-500"
                            />
                        </div>

                        {/* Features */}
                        <div className="flex items-center gap-3">
                            <span className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">Funksjoner:</span>
                            {['android_auto', 'carplay'].map(f => (
                                <button
                                    key={f}
                                    onClick={() => togglePremiumFeature(f)}
                                    className={`px-3 py-1 rounded-lg text-3xs font-black uppercase tracking-widest transition-all ${premiumFeatures.includes(f) ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 'bg-[var(--bg-surface)] text-[var(--text-muted)] border border-[var(--border-primary)]'}`}
                                >
                                    {f === 'android_auto' ? <><Car size={10} className="inline mr-1" />Android Auto</> : <><Car size={10} className="inline mr-1" />CarPlay</>}
                                </button>
                            ))}
                        </div>

                        {/* Save button */}
                        <button
                            onClick={handleSavePremiumConfig}
                            disabled={savingPremium}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-indigo-600/20 border border-indigo-500/30 text-indigo-400 hover:bg-indigo-600/30 transition-all active:scale-95 disabled:opacity-40"
                        >
                            {savingPremium ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                            Lagre Premium-konfig
                        </button>
                    </div>
                </div>
            )}

            {/* Provider Filter */}
            <div className="flex items-center gap-2">
                <Filter size={14} className="text-[var(--text-muted)]" />
                <span className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mr-2">Leverandør:</span>
                {(['all', 'stripe', 'vipps', 'apple'] as ProviderFilter[]).map(f => (
                    <button
                        key={f}
                        onClick={() => setProviderFilter(f)}
                        className={`px-3 py-1.5 rounded-lg text-3xs font-black uppercase tracking-widest transition-all ${providerFilter === f
                            ? f === 'vipps' ? 'bg-vipps/20 text-vipps border border-vipps/30'
                            : f === 'stripe' ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30'
                            : f === 'apple' ? 'bg-stone-500/20 text-[var(--text-secondary)] border border-stone-500/30'
                            : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border border-[var(--border-primary)] hover:bg-[var(--bg-surface)]'
                        }`}
                    >
                        {f === 'all' ? t('common.all') : f === 'stripe' ? 'Stripe' : f === 'vipps' ? 'Vipps' : 'Apple'}
                    </button>
                ))}
            </div>

            {/* Overview Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                            <CreditCard size={16} className="text-emerald-400" />
                        </div>
                        <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Betalende</p>
                    </div>
                    <p className="text-3xl font-black text-[var(--text-primary)]">{getFilteredTotal()}</p>
                    <p className="text-2xs text-[var(--text-muted)] mt-1">
                        Betaler faktisk
                        {report.counts.trial > 0 && providerFilter === 'all' && (
                            <span className="text-amber-400"> · +{report.counts.trial} i prøveperiode</span>
                        )}
                        {(report.counts.familyShared ?? 0) > 0 && providerFilter === 'all' && (
                            <span className="text-sky-400"> · +{report.counts.familyShared} familiedeling</span>
                        )}
                    </p>
                </div>

                <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-stone-500/10 flex items-center justify-center">
                            <Users size={16} className="text-[var(--text-secondary)]" />
                        </div>
                        <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Gratis</p>
                    </div>
                    <p className="text-3xl font-black text-[var(--text-primary)]">{report.counts.free}</p>
                    <p className="text-2xs text-[var(--text-muted)] mt-1">Gratisbrukere</p>
                </div>

                {/* Family Sharing — access without revenue */}
                <div className="bg-[var(--bg-surface-alt)] border border-sky-500/10 p-5 rounded-3xl">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-sky-500/10 flex items-center justify-center">
                            <Users2 size={16} className="text-sky-400" />
                        </div>
                        <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Familiedeling</p>
                    </div>
                    <p className="text-3xl font-black text-[var(--text-primary)]">{report.counts.familyShared ?? 0}</p>
                    <p className="text-2xs text-[var(--text-muted)] mt-1">Apple-tilgang, kr 0 inntekt</p>
                </div>

                <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-violet-500/10 flex items-center justify-center">
                            <TrendingUp size={16} className="text-violet-400" />
                        </div>
                        <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Est. MRR</p>
                    </div>
                    <p className="text-3xl font-black text-[var(--text-primary)]">kr {getFilteredMrr().toLocaleString('nb-NO')}</p>
                    <p className="text-2xs text-[var(--text-muted)] mt-1">{getFilteredTotal()} aktive × plan-pris</p>
                </div>

                <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center">
                            <TrendingUp size={16} className="text-amber-400" />
                        </div>
                        <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Konvertering</p>
                    </div>
                    <p className="text-3xl font-black text-[var(--text-primary)]">{conversionRate().toFixed(1)}%</p>
                    <p className="text-2xs text-[var(--text-muted)] mt-1">Gratis → betalt</p>
                </div>

                {/* Stripe Card */}
                <div className="bg-[var(--bg-surface-alt)] border border-indigo-500/10 p-5 rounded-3xl">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                            <CreditCard size={16} className="text-indigo-400" />
                        </div>
                        <p className="text-2xs font-black text-indigo-400 uppercase tracking-widest">Stripe</p>
                    </div>
                    <p className="text-3xl font-black text-[var(--text-primary)]">{report.counts.stripePaid}</p>
                    <p className="text-2xs text-[var(--text-muted)] mt-1">betalende</p>
                </div>

                {/* Vipps Card */}
                <div className="bg-[var(--bg-surface-alt)] border border-vipps/10 p-5 rounded-3xl">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-vipps/10 flex items-center justify-center">
                            <Smartphone size={16} className="text-vipps" />
                        </div>
                        <p className="text-2xs font-black text-vipps uppercase tracking-widest">Vipps</p>
                    </div>
                    <p className="text-3xl font-black text-[var(--text-primary)]">{report.counts.vippsPaid}</p>
                    <p className="text-2xs text-[var(--text-muted)] mt-1">betalende</p>
                </div>

                {/* Apple Card */}
                <div className="bg-[var(--bg-surface-alt)] border border-stone-500/10 p-5 rounded-3xl">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-stone-500/10 flex items-center justify-center">
                            <Smartphone size={16} className="text-[var(--text-secondary)]" />
                        </div>
                        <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Apple</p>
                    </div>
                    <p className="text-3xl font-black text-[var(--text-primary)]">{report.counts.applePaid}</p>
                    <p className="text-2xs text-[var(--text-muted)] mt-1">betalende</p>
                </div>
            </div>

            {/* Nye brukere per måned (signups — fra samme rapport) */}
            <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-8">
                <div className="flex items-center gap-4 mb-8">
                    <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center">
                        <UserPlus size={20} className="text-indigo-400" />
                    </div>
                    <div>
                        <p className="text-lg font-black text-[var(--text-primary)]">Nye brukere per måned</p>
                        <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">Siste 12 måneder</p>
                    </div>
                </div>

                <div className="h-48 flex items-end gap-2 px-2">
                    {newUsers.length === 0 ? (
                        <div className="w-full h-full flex items-center justify-center border border-dashed border-[var(--border-primary)] rounded-2xl">
                            <p className="text-2xs text-[var(--text-muted)] font-black uppercase tracking-widest">Ingen data</p>
                        </div>
                    ) : (
                        newUsers.map(item => {
                            const height = (item.count / maxNewUsers) * 100;
                            return (
                                <div key={item.month} className="flex-1 h-full group relative flex flex-col items-center justify-end">
                                    {item.count > 0 && (
                                        <span className="text-4xs font-black text-[var(--text-secondary)] mb-1 whitespace-nowrap">{item.count}</span>
                                    )}
                                    <div
                                        className="w-full bg-indigo-500/20 group-hover:bg-indigo-500/40 rounded-t-lg transition-all duration-500 relative min-h-[4px]"
                                        style={{ height: `${height}%` }}
                                    />
                                    <span className="text-4xs text-[var(--text-muted)] font-black uppercase mt-3 absolute -bottom-6 whitespace-nowrap">
                                        {monthLabel(item.month)}
                                    </span>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* ════════ MEDLEMSINNSIKT (levetid · gjentagende · sesong) ════════ */}
            {memberInsights && (
                <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-8 space-y-8">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-2xl bg-teal-500/10 flex items-center justify-center">
                            <Users size={20} className="text-teal-400" />
                        </div>
                        <div>
                            <p className="text-lg font-black text-[var(--text-primary)]">Medlemsinnsikt</p>
                            <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">Levetid · gjentagende kunder · sesong</p>
                        </div>
                    </div>

                    {/* Nøkkeltall */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                            <div className="flex items-center gap-2 mb-2">
                                <Clock size={14} className="text-rose-400" />
                                <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">Snitt levetid</p>
                            </div>
                            <p className="text-2xl font-black text-[var(--text-primary)]">{memberInsights.avgLifespan.toFixed(1)} <span className="text-sm font-bold text-[var(--text-muted)]">mnd</span></p>
                            <p className="text-2xs text-[var(--text-muted)] mt-1">Før de forlot ({memberInsights.churnCount} avsluttede)</p>
                        </div>
                        <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                            <div className="flex items-center gap-2 mb-2">
                                <Clock size={14} className="text-rose-400" />
                                <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">Median levetid</p>
                            </div>
                            <p className="text-2xl font-black text-[var(--text-primary)]">{memberInsights.medianLifespan.toFixed(1)} <span className="text-sm font-bold text-[var(--text-muted)]">mnd</span></p>
                            <p className="text-2xs text-[var(--text-muted)] mt-1">Halvparten kortere/lengre</p>
                        </div>
                        <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                            <div className="flex items-center gap-2 mb-2">
                                <Repeat size={14} className="text-teal-400" />
                                <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">Gjentagende</p>
                            </div>
                            <p className="text-2xl font-black text-[var(--text-primary)]">{memberInsights.recurringPct.toFixed(0)}%</p>
                            <p className="text-2xs text-[var(--text-muted)] mt-1">{memberInsights.recurringCount} av {memberInsights.withHistoryCount} har fornyet</p>
                        </div>
                        <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                            <div className="flex items-center gap-2 mb-2">
                                <CalendarDays size={14} className="text-amber-400" />
                                <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">Topp-måned</p>
                            </div>
                            <p className="text-2xl font-black text-[var(--text-primary)] capitalize">{memberInsights.peakSeason ? memberInsights.peakSeason.name : '–'}</p>
                            <p className="text-2xs text-[var(--text-muted)] mt-1">{memberInsights.peakSeason ? `${memberInsights.peakSeason.avg.toFixed(0)} aktive i snitt` : 'Ingen data'}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Levetid før churn — fordeling */}
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <Clock size={16} className="text-rose-400" />
                                <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest">Betalende levetid før de forlater</p>
                            </div>
                            {memberInsights.churnCount === 0 ? (
                                <div className="py-8 text-center text-tiny text-[var(--text-muted)] border border-dashed border-[var(--border-primary)] rounded-2xl">Ingen avsluttede abonnenter med betalingshistorikk ennå.</div>
                            ) : (
                                <div className="space-y-2.5">
                                    {memberInsights.lifespanBuckets.map(b => (
                                        <div key={b.label} className="flex items-center gap-3">
                                            <span className="text-2xs font-bold text-[var(--text-muted)] w-16 shrink-0 text-right">{b.label}</span>
                                            <div className="flex-1 h-6 bg-[var(--bg-input)] rounded-lg overflow-hidden">
                                                <div className="h-full bg-rose-500/40 rounded-lg transition-all duration-500" style={{ width: `${(b.count / memberInsights.maxBucket) * 100}%`, minWidth: b.count > 0 ? 6 : 0 }} />
                                            </div>
                                            <span className="text-2xs font-black text-[var(--text-secondary)] w-8 shrink-0">{b.count}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Gjentagende vs engangs */}
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <Repeat size={16} className="text-teal-400" />
                                <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest">Gjentagende vs. engangskunder</p>
                                <span className="ml-auto text-3xs text-[var(--text-muted)]">Snitt {memberInsights.avgRenewals.toFixed(1)} betalinger</span>
                            </div>
                            {memberInsights.withHistoryCount === 0 ? (
                                <div className="py-8 text-center text-tiny text-[var(--text-muted)] border border-dashed border-[var(--border-primary)] rounded-2xl">Ingen betalingshistorikk ennå. Kjør «Tilbakefyll».</div>
                            ) : (
                                <>
                                    <div className="flex h-8 rounded-xl overflow-hidden border border-[var(--border-secondary)]">
                                        <div className="bg-teal-500/50 flex items-center justify-center transition-all duration-500" style={{ width: `${(memberInsights.recurringCount / memberInsights.withHistoryCount) * 100}%` }}>
                                            {memberInsights.recurringCount > 0 && <span className="text-3xs font-black text-[var(--text-primary)] px-2">{memberInsights.recurringCount}</span>}
                                        </div>
                                        <div className="bg-stone-500/30 flex items-center justify-center transition-all duration-500" style={{ width: `${(memberInsights.oneTimeCount / memberInsights.withHistoryCount) * 100}%` }}>
                                            {memberInsights.oneTimeCount > 0 && <span className="text-3xs font-black text-[var(--text-secondary)] px-2">{memberInsights.oneTimeCount}</span>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 mt-3">
                                        <span className="flex items-center gap-1.5 text-3xs font-bold text-teal-400"><span className="w-2.5 h-2.5 rounded bg-teal-500/50" /> Gjentagende ({'>'}1 betaling)</span>
                                        <span className="flex items-center gap-1.5 text-3xs font-bold text-[var(--text-secondary)]"><span className="w-2.5 h-2.5 rounded bg-stone-500/40" /> Engangs (1 betaling)</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Sesongmønster jan–des */}
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <CalendarDays size={16} className="text-amber-400" />
                            <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest">Aktive medlemmer per måned (sesong)</p>
                            <span className="ml-auto text-3xs text-[var(--text-muted)]">Snitt over år</span>
                        </div>
                        <div className="h-40 flex items-end gap-2 px-1">
                            {memberInsights.seasonality.map(m => {
                                const height = (m.avg / memberInsights.maxSeason) * 100;
                                const isPeak = memberInsights.peakSeason && m.name === memberInsights.peakSeason.name && m.avg > 0;
                                return (
                                    <div key={m.name} className="flex-1 h-full group relative flex flex-col items-center justify-end">
                                        {m.avg > 0 && <span className="text-4xs font-black text-[var(--text-secondary)] mb-1 whitespace-nowrap">{m.avg.toFixed(0)}</span>}
                                        <div
                                            className={`w-full rounded-t-lg transition-all duration-500 min-h-[4px] ${isPeak ? 'bg-amber-500/60 group-hover:bg-amber-500/80' : 'bg-amber-500/20 group-hover:bg-amber-500/40'}`}
                                            style={{ height: `${height}%` }}
                                        />
                                        <span className="text-4xs text-[var(--text-muted)] font-black uppercase mt-2 capitalize">{m.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* ════════ FAKTISK INNTEKT & ABONNENTER (server-aggregert) ════════ */}
            <div className="bg-[var(--bg-surface-alt)] border border-emerald-500/20 rounded-panel p-8 space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                            <Receipt size={20} className="text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-lg font-black text-[var(--text-primary)]">Faktisk inntekt &amp; abonnenter</p>
                            <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">
                                Reelle betalinger fra Stripe, Vipps og Apple
                                {report && <span className="text-[var(--text-muted)] normal-case"> · {report.transactionCount} transaksjoner</span>}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={loadReport} disabled={reportLoading}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[var(--bg-surface)] hover:bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-[var(--text-secondary)] text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50">
                            <RefreshCw size={12} className={reportLoading ? 'animate-spin' : ''} /> Oppdater
                        </button>
                        <button onClick={exportMonthlyCsv} disabled={!report}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-400 text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50">
                            <Download size={12} /> CSV måned
                        </button>
                        <button onClick={exportTransactionsCsv} disabled={!report}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-400 text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50">
                            <Download size={12} /> CSV bilag
                        </button>
                        <button onClick={handleReconcile} disabled={reconciling}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/20 text-violet-400 text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50"
                            title="Henter autoritativ Apple-eierskap (familiedeling vs kjøpt) og inntekt fra RevenueCat">
                            {reconciling ? <Loader2 size={12} className="animate-spin" /> : <Activity size={12} />} Avstem
                        </button>
                        <button onClick={handleBackfill} disabled={backfilling}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-400 text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50">
                            {backfilling ? <Loader2 size={12} className="animate-spin" /> : <Database size={12} />} Tilbakefyll
                        </button>
                    </div>
                </div>

                {backfillResult && (
                    <div className="text-tiny text-[var(--text-secondary)] bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-4 py-3">{backfillResult}</div>
                )}
                {reportError && (
                    <div className="flex items-center gap-2 text-tiny text-red-400 bg-red-500/5 border border-red-500/20 rounded-xl px-4 py-3">
                        <AlertCircle size={14} /> {reportError}
                    </div>
                )}

                {reportLoading && !report ? (
                    <div className="flex items-center justify-center py-12"><Loader2 size={20} className="animate-spin text-[var(--text-muted)]" /></div>
                ) : report && (
                    <>
                        {/* Reelle inntektstall (tellinger vises i toppkortene — én sannhetskilde) */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                                <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-2">Inntekt totalt</p>
                                <p className="text-2xl font-black text-[var(--text-primary)]">{fmtNok(totalRevenue())}</p>
                                <p className="text-2xs text-[var(--text-muted)] mt-1">Alle bekreftede betalinger</p>
                            </div>
                            <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] p-5 rounded-3xl">
                                <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-2">Siste 30 dager</p>
                                <p className="text-2xl font-black text-emerald-400">{fmtNok(report.last30Revenue)}</p>
                                <p className="text-2xs text-[var(--text-muted)] mt-1">Faktisk omsetning</p>
                            </div>
                            {report.appleRevenue && report.appleRevenue.grossUsd > 0 && (
                                <div className="bg-[var(--bg-input)] border border-stone-500/15 p-5 rounded-3xl">
                                    <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-2">Apple netto (RevenueCat)</p>
                                    <p className="text-2xl font-black text-[var(--text-primary)]">${report.appleRevenue.proceedsUsd.toFixed(2)}</p>
                                    <p className="text-2xs text-[var(--text-muted)] mt-1">Etter Apple-kutt · ${report.appleRevenue.grossUsd.toFixed(2)} brutto (livstid)</p>
                                </div>
                            )}
                        </div>

                        {/* Inntekt per år */}
                        {report.revenue.yearly.length > 0 && (
                            <div>
                                <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-3">Inntekt per år</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {report.revenue.yearly.map(y => (
                                        <div key={y.year} className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl p-4">
                                            <p className="text-xs font-black text-[var(--text-primary)]">{y.year}</p>
                                            <p className="text-lg font-black text-emerald-400 mt-1">{fmtNok(y.total)}</p>
                                            <p className="text-3xs text-[var(--text-muted)] mt-1">{y.count} betalinger</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Inntekt per måned per tjeneste */}
                        <div className="overflow-x-auto rounded-2xl border border-[var(--border-secondary)]">
                            <table className="w-full text-left min-w-[640px]">
                                <thead>
                                    <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-input)]">
                                        <th className="px-5 py-3 text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">Måned</th>
                                        <th className="px-4 py-3 text-3xs font-black text-indigo-400/70 uppercase tracking-widest text-right">Stripe</th>
                                        <th className="px-4 py-3 text-3xs font-black text-vipps/70 uppercase tracking-widest text-right">Vipps</th>
                                        <th className="px-4 py-3 text-3xs font-black text-[var(--text-secondary)]/70 uppercase tracking-widest text-right">Apple</th>
                                        <th className="px-5 py-3 text-3xs font-black text-emerald-400 uppercase tracking-widest text-right">Total</th>
                                        <th className="px-5 py-3 text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest text-right">Betalinger</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.revenue.monthly.length === 0 ? (
                                        <tr><td colSpan={6} className="px-5 py-8 text-center text-tiny text-[var(--text-muted)]">Ingen transaksjoner ennå. Kjør «Tilbakefyll» for å hente historikk.</td></tr>
                                    ) : [...report.revenue.monthly].reverse().map(m => (
                                        <tr key={m.month} className="border-b border-[var(--border-primary)] hover:bg-[var(--bg-input)]/50 transition-colors">
                                            <td className="px-5 py-3 text-xs font-bold text-[var(--text-primary)]">{m.month}</td>
                                            <td className="px-4 py-3 text-xs text-indigo-400 text-right">{fmtNok(m.stripe)}</td>
                                            <td className="px-4 py-3 text-xs text-vipps text-right">{fmtNok(m.vipps)}</td>
                                            <td className="px-4 py-3 text-xs text-[var(--text-secondary)] text-right">{fmtNok(m.apple)}</td>
                                            <td className="px-5 py-3 text-xs font-black text-emerald-400 text-right">{fmtNok(m.total)}</td>
                                            <td className="px-5 py-3 text-xs text-[var(--text-muted)] text-right">{m.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Ukentlig opprettet / oppsagt */}
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <Activity size={16} className="text-pink-400" />
                                <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest">Abonnement opprettet / oppsagt per uke</p>
                                <div className="flex items-center gap-3 ml-auto">
                                    <span className="flex items-center gap-1.5 text-3xs font-bold text-emerald-400"><span className="w-2.5 h-2.5 rounded bg-emerald-500/50" /> Opprettet</span>
                                    <span className="flex items-center gap-1.5 text-3xs font-bold text-red-400"><span className="w-2.5 h-2.5 rounded bg-red-500/50" /> Oppsagt</span>
                                </div>
                            </div>
                            {report.weekly.length === 0 ? (
                                <div className="py-8 text-center text-tiny text-[var(--text-muted)] border border-dashed border-[var(--border-primary)] rounded-2xl">Ingen hendelser ennå. Logges fremover + ved tilbakefylling.</div>
                            ) : (() => {
                                const recent = report.weekly.slice(-26);
                                const maxW = Math.max(...recent.map(w => Math.max(w.created, w.cancelled)), 1);
                                return (
                                    <div className="overflow-x-auto pb-6">
                                        <div className="flex items-end gap-1.5 h-40 min-w-max px-1">
                                            {recent.map(w => (
                                                <div key={w.week} className="flex flex-col items-center justify-end gap-0.5 group relative" style={{ width: 18 }}>
                                                    <div className="flex items-end gap-0.5 h-32">
                                                        <div className="w-2 bg-emerald-500/50 rounded-t group-hover:bg-emerald-500/70 transition-all" style={{ height: `${(w.created / maxW) * 100}%`, minHeight: w.created > 0 ? 2 : 0 }} />
                                                        <div className="w-2 bg-red-500/50 rounded-t group-hover:bg-red-500/70 transition-all" style={{ height: `${(w.cancelled / maxW) * 100}%`, minHeight: w.cancelled > 0 ? 2 : 0 }} />
                                                    </div>
                                                    <span className="text-[7px] text-[var(--text-muted)] font-bold rotate-45 origin-left whitespace-nowrap mt-1 absolute -bottom-4 left-1/2">{w.week.slice(5)}</span>
                                                    <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-[var(--bg-input)] border border-[var(--border-secondary)] text-4xs font-black px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 space-x-1.5">
                                                        <span className="text-emerald-400">+{w.created}</span><span className="text-red-400">−{w.cancelled}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Abonnentliste */}
                        <div>
                            <div className="flex items-center gap-3 mb-4 flex-wrap">
                                <Users size={16} className="text-[var(--text-secondary)]" />
                                <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest">
                                    Abonnenter ({report.subscribers.filter(s => !activeOnly || s.active).length}{activeOnly ? ` aktive` : ''})
                                </p>
                                <div className="flex items-center gap-2 ml-auto">
                                    <button
                                        onClick={() => setActiveOnly(v => !v)}
                                        className={`px-3 py-1.5 rounded-xl text-2xs font-black uppercase tracking-widest transition-all active:scale-95 border ${activeOnly ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-[var(--bg-input)] text-[var(--text-muted)] border-[var(--border-secondary)] hover:text-[var(--text-secondary)]'}`}
                                    >
                                        Kun aktive
                                    </button>
                                    <div className="relative">
                                        <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                                        <input value={subSearch} onChange={e => setSubSearch(e.target.value)} placeholder="Søk navn / e-post"
                                            className="bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl pl-8 pr-3 py-1.5 text-tiny text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-emerald-500/30 w-48" />
                                    </div>
                                </div>
                            </div>
                            <div className="overflow-x-auto rounded-2xl border border-[var(--border-secondary)]">
                                <table className="w-full text-left min-w-[720px]">
                                    <thead>
                                        <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-input)]">
                                            <th className="px-5 py-3 text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">Abonnent</th>
                                            <th className="px-4 py-3 text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">Tjeneste</th>
                                            <th className="px-4 py-3 text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">Status</th>
                                            <th className="px-4 py-3 text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">Siden</th>
                                            <th className="px-4 py-3 text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest text-right">Mnd</th>
                                            <th className="px-5 py-3 text-3xs font-black text-emerald-400 uppercase tracking-widest text-right">Totalt betalt</th>
                                            <th className="px-3 py-3" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.subscribers
                                            .filter(s => !activeOnly || s.active)
                                            .filter(s => !subSearch || (s.name || '').toLowerCase().includes(subSearch.toLowerCase()) || (s.email || '').toLowerCase().includes(subSearch.toLowerCase()))
                                            .map(s => (
                                            <React.Fragment key={s.uid}>
                                                <tr onClick={() => toggleSubscriber(s.uid)} className="border-b border-[var(--border-primary)] hover:bg-[var(--bg-input)]/50 transition-colors cursor-pointer">
                                                    <td className="px-5 py-3">
                                                        <p className="text-xs font-bold text-[var(--text-primary)]">{s.name || '(uten navn)'}</p>
                                                        <p className="text-2xs text-[var(--text-muted)]">{s.email || s.uid.slice(0, 12)}</p>
                                                    </td>
                                                    <td className="px-4 py-3 text-tiny font-bold text-[var(--text-secondary)]">{providerLabel(s.provider)}{s.plan === 'premium' && <span className="text-indigo-400"> · Premium</span>}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`text-3xs font-black uppercase tracking-widest px-2 py-0.5 rounded-md border ${
                                                            s.status === 'paid' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                                            : s.status === 'trial' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                                            : s.status === 'test' ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                                            : s.status === 'churned' ? 'bg-[var(--bg-surface)] text-[var(--text-muted)] border-[var(--border-secondary)]'
                                                            : 'bg-stone-500/10 text-[var(--text-secondary)] border-stone-500/20'
                                                        }`}>
                                                            {s.status === 'paid' ? 'Aktiv' : s.status === 'trial' ? 'Prøve' : s.status === 'test' ? 'Test' : s.status === 'churned' ? 'Avsluttet' : s.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-tiny text-[var(--text-secondary)]">{fmtDate(s.startDate)}</td>
                                                    <td className="px-4 py-3 text-tiny text-[var(--text-secondary)] text-right">{monthsActive(s.startDate, s.active ? null : s.currentPeriodEnd)}</td>
                                                    <td className="px-5 py-3 text-xs font-black text-emerald-400 text-right">{fmtNok(s.totalPaidNok)}{s.unknownFx && <span title="Ukjent valutakurs — sett kurs i system/fx_rates" className="text-amber-400">*</span>}</td>
                                                    <td className="px-3 py-3 text-[var(--text-muted)]">{expandedUid === s.uid ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                                                </tr>
                                                {expandedUid === s.uid && (
                                                    <tr className="bg-[var(--bg-input)]/40">
                                                        <td colSpan={7} className="px-5 py-4">
                                                            {loadingPayments === s.uid ? (
                                                                <div className="flex items-center gap-2 text-tiny text-[var(--text-muted)]"><Loader2 size={12} className="animate-spin" /> Henter betalingshistorikk…</div>
                                                            ) : (userPayments[s.uid]?.length ? (
                                                                <div className="space-y-1.5">
                                                                    {userPayments[s.uid].map(t => (
                                                                        <div key={t.id} className="flex items-center justify-between gap-4 text-tiny py-1.5 border-b border-[var(--border-primary)]/50 last:border-0">
                                                                            <span className="text-[var(--text-muted)] w-24 shrink-0">{fmtDate(t.date)}</span>
                                                                            <span className="text-[var(--text-secondary)] flex-1">{t.description}</span>
                                                                            <span className="text-3xs font-black uppercase text-[var(--text-muted)]">{providerLabel(t.provider)}</span>
                                                                            <span className="text-[var(--text-primary)] font-bold w-28 text-right">{t.amount.toLocaleString('nb-NO')} {t.currency}{t.amountNok != null && t.currency !== 'NOK' ? ` (${fmtNok(t.amountNok)})` : ''}</span>
                                                                            <span className={`text-3xs font-black uppercase w-16 text-right ${t.status === 'paid' ? 'text-emerald-400' : 'text-[var(--text-muted)]'}`}>{t.status}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <p className="text-tiny text-[var(--text-muted)]">Ingen registrerte transaksjoner. Kjør «Tilbakefyll» for å hente historikk.</p>
                                                            ))}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminRevenueDashboard;
